import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { Logo } from "@/components/Logo";
import { Mail, MapPin, Phone } from "lucide-react";
import { updateConsent, CONSENT_DEFAULTS } from "@/lib/consent";

export default function Footer({ onManageCookies }) {
  const { lang, t } = useI18n();
  const explore = t("footer.explore");
  const legalLinks = t("footer.legalLinks");
  const cb = t("footer.companyBlock");
  const location = useLocation();
  const isHome = location.pathname === `/${lang}` || location.pathname === `/${lang}/`;

  const buildHref = (hash) => (isHome ? hash : `/${lang}${hash}`);

  return (
    <footer className="ecominerali-footer bg-[#1C352D] text-[#EAEFEA]" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <Logo size={44} />
              <div>
                <div className="font-serif text-xl">Ecominerali</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#D4AF37]">Milano · dal 2019</div>
              </div>
            </div>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-[#EAEFEA]/75">{t("footer.tagline")}</p>

            <div className="mt-8 space-y-2 text-[13px] text-[#EAEFEA]/80">
              <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-[3px] text-[#D4AF37]" /><span>{cb.address}</span></div>
              <div className="flex items-start gap-2"><Mail className="w-3.5 h-3.5 mt-[3px] text-[#D4AF37]" /><a className="hover:text-[#D4AF37]" href={`mailto:${cb.email}`} data-testid="footer-email">{cb.email}</a></div>
              <div className="flex items-start gap-2"><Phone className="w-3.5 h-3.5 mt-[3px] text-[#D4AF37]" /><a className="hover:text-[#D4AF37]" href={`tel:${cb.phone.replace(/\s+/g, "")}`} data-testid="footer-phone">{cb.phone}</a></div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#D4AF37] mb-4">{t("footer.columns.explore")}</div>
            <ul className="space-y-2.5 text-[14px]">
              {explore.map((it, i) => (
                <li key={i}>
                  <a href={buildHref(it.href)} className="hover:text-[#D4AF37] transition-colors" data-testid={`footer-explore-${i}`}>{it.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#D4AF37] mb-4">{t("footer.columns.company")}</div>
            <ul className="space-y-2 text-[12.5px] text-[#EAEFEA]/80 leading-relaxed">
              <li className="font-medium text-[#EAEFEA]">{cb.name}</li>
              <li>{cb.founded}</li>
              <li>{cb.piva}</li>
              <li>{cb.rea}</li>
              <li>{cb.capital}</li>
              <li>{cb.license}</li>
              <li>PEC: <a className="hover:text-[#D4AF37]" href={`mailto:${cb.pec}`} data-testid="footer-pec">{cb.pec}</a></li>
              <li>{cb.jurisdiction}</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-[#EAEFEA]/10 grid grid-cols-1 md:grid-cols-12 gap-6">
          <p className="md:col-span-8 text-[11.5px] leading-relaxed text-[#EAEFEA]/60">{t("footer.regulatory")}</p>
          <div className="md:col-span-4 flex md:justify-end">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#D4AF37]/80">{t("footer.columns.legal")}</div>
          </div>
        </div>

        <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-[11px] text-[#EAEFEA]/60">{t("footer.copyright")}</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-[12px]">
            {legalLinks.map((l, i) => (
              <Link key={l.path} to={`/${lang}/${l.path}`} className="hover:text-[#D4AF37]" data-testid={`footer-legal-${l.path}`}>
                {l.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={onManageCookies}
              className="hover:text-[#D4AF37] underline-offset-4 hover:underline"
              data-testid="footer-manage-cookies"
            >
              {t("cookieBanner.manage")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
